import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

/**
 * Naturvårdsverket INSPIRE WMS layers for the auction details map only.
 * Stacked in tilePane with z-index above the basemap (1) and below vector overlays (GeoJSON etc.).
 *
 * proxyName values must match backend LeafletWMSProxy whitelist (apps/main/endpoints/leaflet.py).
 *
 * ps-n2k: GetCapabilities exposes PS.ProtectedSites.Natura2000 (URLs without ?layers= were ambiguous).
 */
export const AUCTION_PROTECTED_SITES_WMS = [
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-ic/ows', layers: 'PS.ProtectedSites.Ramsar', proxyName: 'auction-nv-ramsar', label: 'Ramsar' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.KR', proxyName: 'auction-nv-kr', label: 'Kulturreservat (KR)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.NM', proxyName: 'auction-nv-nm', label: 'Naturminne (NM)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.NVO', proxyName: 'auction-nv-nvo', label: 'Naturvårdsområde (NVO)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.OBO', proxyName: 'auction-nv-obo', label: 'Övrigt biotopskyddsområde (OBO)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.NP', proxyName: 'auction-nv-np', label: 'Nationalpark (NP)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/am-restriction/ows', layers: 'AM.regulatoryAreas', proxyName: 'auction-nv-am-regulatory', label: 'Regleringsområden' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-n2k/ows', layers: 'PS.ProtectedSites.Natura2000', proxyName: 'auction-nv-natura2000', label: 'Natura 2000' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.DVO', proxyName: 'auction-nv-dvo', label: 'Djur- och växtartsområde (DVO)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.NR', proxyName: 'auction-nv-nr', label: 'Naturreservat (NR)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/ps-nvr/ows', layers: 'PS.ProtectedSites.IF', proxyName: 'auction-nv-if', label: 'IF (skyddat område)' },
    { url: 'https://geodata.naturvardsverket.se/inspire/am-restriction/ows', layers: 'AM.drinkingWaterProtectionArea', proxyName: 'auction-nv-am-drinking', label: 'Dricksvattenskyddsområde' },
];

const BASE_Z_INDEX = 10;

/**
 * Repair text when UTF-8 bytes were read as Latin-1 (e.g. "Ã¥" instead of "å").
 * Used as a fallback if the proxy still returns mis-decoded strings.
 */
function fixUtf8Mojibake(str) {
    if (typeof str !== 'string' || str.length === 0) return str;
    if (!/[ÃÂ]/.test(str)) return str;
    try {
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xff;
        const fixed = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        if (fixed.includes('\uFFFD')) return str;
        return fixed;
    } catch {
        return str;
    }
}

/**
 * Query string for GetFeatureInfo.
 * WMS 1.3.x requires CRS (not SRS); Naturvårdsverket returns "GetMap CRS parameter is mandatory" otherwise.
 * For WMS 1.3 + CRS=EPSG:4326, BBOX axis order must follow EPSG (latitude, longitude): south,west,north,east.
 * Leaflet's toBBoxString() is west,south,east,north (lon,lat) and breaks empty FeatureCollection responses.
 */
function buildGetFeatureInfoArgs(map, wmsLayer, latlng) {
    const point = map.latLngToContainerPoint(latlng, map.getZoom());
    const size = map.getSize();
    const wp = wmsLayer.wmsParams;
    const version = wp.version || '1.3.0';
    const bounds = map.getBounds();
    const params = {
        request: 'GetFeatureInfo',
        service: 'WMS',
        styles: wp.styles !== undefined && wp.styles !== null ? wp.styles : '',
        transparent: wp.transparent,
        version,
        format: wp.format,
        height: size.y,
        width: size.x,
        layers: wp.layers,
        query_layers: wp.layers,
        info_format: 'text/xml',
    };
    if (version === '1.3.0') {
        params.crs = 'EPSG:4326';
        params.bbox = [bounds.getSouth(), bounds.getWest(), bounds.getNorth(), bounds.getEast()].join(',');
        params.i = Math.floor(point.x);
        params.j = Math.floor(point.y);
    } else {
        params.srs = 'EPSG:4326';
        params.bbox = bounds.toBBoxString();
        params.x = Math.floor(point.x);
        params.y = Math.floor(point.y);
    }
    return L.Util.getParamString(params, wmsLayer._url, true);
}

async function fetchFeatureInfoViaProxy(proxyName, args) {
    const resp = await fetch('/leaflet/wms-proxy/', {
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify({ name: proxyName, args }),
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken'),
        },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.xmlString ?? null;
}

function collectRowsFromElement(el, rows, keyPrefix = '') {
    if (!el || el.nodeType !== 1) return;
    if (el.attributes && el.attributes.length) {
        for (let i = 0; i < el.attributes.length; i++) {
            const a = el.attributes[i];
            if (a.name === 'xmlns' || a.name.startsWith('xmlns:')) continue;
            rows.push([keyPrefix + a.name, a.value]);
        }
    }
    for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i];
        const text = child.textContent ? child.textContent.trim() : '';
        if (child.children.length === 0 && text) {
            rows.push([keyPrefix + child.localName, text]);
        } else if (child.children.length > 0) {
            collectRowsFromElement(child, rows, keyPrefix + child.localName + ' › ');
        }
    }
}

/** Hide GML geometry paths (noisy / huge); keys are built like "geom › Polygon › …". */
function isHiddenWmsDisplayKey(key) {
    const k = typeof key === 'string' ? key.trim() : '';
    return k.startsWith('geom ›');
}

/**
 * Returns non-null when the response has identifiable feature content.
 */
function parseNvFeatureInfoXml(xmlString) {
    if (!xmlString || typeof xmlString !== 'string') return null;
    const trimmed = xmlString.trim();
    if (!trimmed) return null;

    const doc = new DOMParser().parseFromString(trimmed, 'application/xml');
    if (doc.querySelector('parsererror')) return null;

    const se = doc.getElementsByTagNameNS('http://www.opengis.net/ogc', 'ServiceException');
    if (se.length) return null;

    const rows = [];

    const fieldsNodes = doc.getElementsByTagName('FIELDS');
    for (let i = 0; i < fieldsNodes.length; i++) {
        collectRowsFromElement(fieldsNodes[i], rows, '');
    }

    const anyNs = doc.getElementsByTagName('*');
    const members = [];
    for (let i = 0; i < anyNs.length; i++) {
        if (anyNs[i].localName === 'featureMember') members.push(anyNs[i]);
    }
    for (let m = 0; m < members.length; m++) {
        const fm = members[m];
        for (let c = 0; c < fm.children.length; c++) {
            collectRowsFromElement(fm.children[c], rows, '');
        }
    }

    if (!rows.length) return null;

    const seen = new Set();
    const unique = [];
    for (let i = 0; i < rows.length; i++) {
        const k = rows[i][0];
        const v = rows[i][1];
        if (!v || !String(v).trim()) continue;
        if (isHiddenWmsDisplayKey(k)) continue;
        const key = `${k}::${v}`;
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push([fixUtf8Mojibake(k), fixUtf8Mojibake(v)]);
    }
    return unique.length ? unique : null;
}

/**
 * @param {React.RefObject} mapRef
 * @param {object} [options]
 * @param {() => void} [options.onIdentifyStart] — before parallel GetFeatureInfo requests
 * @param {(sections: Array<{ label: string, rows: [string, string][] }>) => void} [options.onIdentifyComplete] — always called when requests finish (sections may be empty)
 * @param {() => boolean} [options.shouldRunIdentify] — if set and returns false, the click is ignored (e.g. Sell Timber draw / marker modes must not open the identify UI).
 */
export function useAuctionProtectedSitesWms(mapRef, options = {}) {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        let cancelled = false;
        let installed = false;
        const added = [];
        let mapForCleanup = null;
        let mapClickHandler = null;

        const addAll = () => {
            if (installed) return true;
            const map = mapRef.current?.state?.map;
            if (!map || typeof L === 'undefined') return false;
            mapForCleanup = map;

            AUCTION_PROTECTED_SITES_WMS.forEach((def, i) => {
                const wms = L.tileLayer.wms(def.url, {
                    layers: def.layers,
                    transparent: true,
                    format: 'image/png',
                    tiled: true,
                    version: '1.3.0',
                });
                wms.setZIndex(BASE_Z_INDEX + i);
                wms.addTo(map);
                added.push({ def, layer: wms });
            });

            mapClickHandler = async (e) => {
                if (cancelled) return;
                const { onIdentifyStart, onIdentifyComplete, shouldRunIdentify } = optionsRef.current;
                if (typeof shouldRunIdentify === 'function' && !shouldRunIdentify()) {
                    return;
                }
                onIdentifyStart?.();

                const argsList = added.map(({ def, layer }) => ({
                    def,
                    args: buildGetFeatureInfoArgs(map, layer, e.latlng),
                }));

                let sections = [];
                try {
                    const results = await Promise.all(
                        argsList.map(async ({ def, args }) => {
                            try {
                                const xml = await fetchFeatureInfoViaProxy(def.proxyName, args);
                                const rows = parseNvFeatureInfoXml(xml);
                                return rows ? { label: def.label, rows } : null;
                            } catch (err) {
                                console.error('auction WMS identify', def.proxyName, err);
                                return null;
                            }
                        })
                    );
                    sections = results.filter(Boolean);
                } catch (err) {
                    console.error('auction WMS identify', err);
                }

                onIdentifyComplete?.(sections);
            };

            map.on('click', mapClickHandler);
            installed = true;
            return true;
        };

        let interval = null;
        let timeout = null;
        if (!addAll()) {
            interval = setInterval(() => {
                if (cancelled) return;
                if (addAll() && interval) clearInterval(interval);
            }, 50);
            timeout = setTimeout(() => {
                if (interval) clearInterval(interval);
            }, 3000);
        }

        return () => {
            cancelled = true;
            if (interval) clearInterval(interval);
            if (timeout) clearTimeout(timeout);
            const m = mapForCleanup || mapRef.current?.state?.map;
            if (m && mapClickHandler) {
                m.off('click', mapClickHandler);
            }
            added.forEach(({ layer }) => {
                if (m?.hasLayer(layer)) m.removeLayer(layer);
            });
        };
    }, [mapRef]);
}

/** Same hook — use this name outside the auction page (e.g. SellTimber map). */
export { useAuctionProtectedSitesWms as useNvProtectedSitesWms };
